import { useState, useEffect, useMemo } from 'react';
import _ from 'lodash';
import { Formik, Form } from 'formik';
import Yup from '../../utils/yup';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

import useMenus from '../../context/MenuContext';
import UserReports from './UserReports';
import { DeleteDialogUI, FormBase, FormButtonPanelUI, FormHeaderUI } from '../Interface';
import userService from '../../service/user.service';
import { FormFields } from './FormFields';
import useUsers from '../../context/UsersContext';
import appService from '../../service/app.service';
import biService from '../../service/bi.service';
import dashboardService from '../../service/dashboard.service';
import UserTabs from './UserTabs';
import masterDataService from '../../service/masterData.service';
import { generateAutocompleteItems } from '../../utils/helper';
import useParam from '../../context/ParamContext';

export const UserForm = () => {
  const { getUser, user } = useUsers();
  const { params, getParam } = useParam();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit, getSubMenuNamesByUrls } = useMenus();
  const [submenu, setSubmenu] = useState(null);
  const [refetchTable, setRefetchTable] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);

  const [defaultValues, setDefaultValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [selectedValues, setSelectedValues] = useState({});
  const [initValues, setInitValues] = useState({});
  const [autoCompleteData, setAutoCompleteData] = useState([]);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    resourceOne: [],
    roleList: [],
    webUserArchive: [],
    companyLocation: [],
    employees: [],
  });
  const [userGroupUserLink, setUserGroupUserLink] = useState([]);
  const [projectTypeUserLink, setProjectTypeUserLink] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const def = {
    userId: null,
    userName: '',
    password: '',
    resourceOneId: null,
    pinCode: '',
    roleId: null,
    nickName: '',
    activeId: 1,
    homePage: null,
    costPerHour: '',
    color: '',
    technologist: false,
    groupLeader: false,
    email: '',
    phoneNumber: '',
    notifyOnNewProject: false,
    companyLocationId: null,
    employeeId: null,
  };

  useEffect(() => {
    setDefaultValues(def);
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(params).length) getParam();
    getMenus();
    getAllAutoCompleteData();
    initDefaultValues();
    getOperationLinks();
    getProjectTypeLinks();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('user');
    setSubmenu(submenuId);
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!_.isEqual(defaultValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        valuesToForm();
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const getOperationLinks = () => {
    masterDataService.userGroupsToForm().then((data) => {
      const preparedData = data.map((group) => ({
        ...group,
        checked: false,
      }));

      setUserGroupUserLink(preparedData);
      setInitValues((prev) => ({ ...prev, userGroupUserLink: preparedData }));
      setDefaultValues((prev) => ({ ...prev, userGroupUserLink: preparedData }));
    });
  };

  const getProjectTypeLinks = () => {
    masterDataService.projectTypeToForm().then((data) => {
      const preparedData = data.map((group) => ({
        ...group,
        checked: false,
      }));

      setProjectTypeUserLink(preparedData);
      setInitValues((prev) => ({ ...prev, projectTypeUserLink: preparedData }));
      setDefaultValues((prev) => ({ ...prev, projectTypeUserLink: preparedData }));
    });
  };

  const getAllAutoCompleteData = () => {
    Promise.all([appService.getAllSubMenus(), biService.allBiReports(), dashboardService.allDashboards()])
      .then(([submenuData, biReportData, dashboardData]) => {
        //@TODO maybe roles
        const formattedSubmenus = submenuData.map((submenu) => ({
          label: submenu.subMenuName,
          value: submenu.url,
        }));
        const formattedBiReports = biReportData.map((report) => ({
          label: `R - ${report.name}`,
          value: `bi/biPage/${report.id}`,
        }));

        const formattedDashboards = dashboardData.map((dashboard) => ({
          label: `D - ${dashboard.name}`,
          value: `dashboard/${dashboard.id}`,
        }));

        const mergedData = [...formattedSubmenus, ...formattedBiReports, ...formattedDashboards];

        setAutoCompleteData(mergedData);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      });
  };

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .rolesToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .resourceOneToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .webUserArchiveToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyLocationToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .employeesToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          roleList: resp[0],
          resourceOne: resp[1],
          webUserArchive: resp[2],
          companyLocation: resp[3],
          employees: resp[4],
        };

        const filteredResourceOne = aData?.resourceOne?.filter((r1) => r1.active);
        const acOptions = {
          roleList: generateAutocompleteItems(aData.roleList, 'roleName', 'roleId'),
          resourceOne: generateAutocompleteItems(filteredResourceOne, 'name', 'id'),
          webUserArchive: generateAutocompleteItems(aData.webUserArchive, 'arhiveName', 'archiveId'),
          companyLocation: generateAutocompleteItems(aData.companyLocation, 'name', 'id'),
          employees: generateAutocompleteItems(aData.employees, 'name', 'id'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const validationSchema = Yup.object().shape({
    userId: Yup.string().nullable(),
    userName: Yup.string().required(),
    password: Yup.string().required(),
    resourceOneId: Yup.string().nullable(),
    pinCode: Yup.string().nullable().min(4).max(4),
    roleId: Yup.string().required(),
    nickName: Yup.string(),
    activeId: Yup.string().nullable(),
    homePage: Yup.string().nullable(),
    costPerHour: Yup.number().positive().nullable(),
    color: Yup.string()
      .nullable()
      .matches(/^$|^#(?:[0-9a-fA-F]{3}){1,2}$/)
      .max(7),
    technologist: Yup.boolean(),
    groupLeader: Yup.boolean(),
    userGroupUserLink: Yup.array().of(
      Yup.object().shape({
        id: Yup.number().required(),
        name: Yup.string().required(),
        checked: Yup.boolean().required(),
      })
    ),
    projectTypeUserLink: Yup.array().of(
      Yup.object().shape({
        id: Yup.number().required(),
        name: Yup.string().required(),
        checked: Yup.boolean().required(),
      })
    ),
    email: Yup.string().email('Érvénytelen email cím').nullable(),
    phoneNumber: Yup.string().nullable(),
    notifyOnNewProject: Yup.boolean(),
    companyLocationId: Yup.string().nullable(),
    employeeId: Yup.string().nullable(),
  });

  const valuesToForm = () => {
    const {
      userId,
      userName,
      password,
      resourceOneId,
      pinCode,
      roleId,
      nickName,
      activeId,
      homePage,
      costPerHour,
      color,
      technologist,
      groupLeader,
      userGroupUserLink: userGroupUserLinkValues,
      projectTypeUserLink: projectTypeUserLinkValues,
      email,
      phoneNumber,
      notifyOnNewProject,
      companyLocationId,
      employeeId,
    } = selectedValues;

    const updatedUserLink = userGroupUserLink?.map((userGroup) => ({
      ...userGroup,
      checked: userGroupUserLinkValues?.some((form) => form.userGroupId === userGroup.id) || false,
    }));

    const updatedProjectTypeUserLink = projectTypeUserLink?.map((projectTypeUser) => ({
      ...projectTypeUser,
      checked: projectTypeUserLinkValues?.some((form) => form.projectTypeId === projectTypeUser.id) || false,
    }));

    setInitValues({
      userId,
      userName,
      password,
      pinCode: pinCode ?? defaultValues.pinCode,
      resourceOneId,
      roleId,
      nickName,
      activeId,
      homePage,
      costPerHour: costPerHour ?? defaultValues.costPerHour,
      color: color ?? defaultValues.color,
      technologist: technologist ?? defaultValues.technologist,
      groupLeader,
      userGroupUserLink: updatedUserLink,
      projectTypeUserLink: updatedProjectTypeUserLink,
      email: email ?? '',
      phoneNumber: phoneNumber ?? '',
      notifyOnNewProject,
      companyLocationId,
      employeeId,
    });
  };

  const haveSelectedValues = () => {
    return Boolean(Object.keys(selectedValues).length);
  };

  const handleSubmit = (values) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values);
    }
  };

  const updateObject = async (values) => {
    const updData = {
      ...values,
      updatedBy: user.userId,
      costPerHour: parseFloat(values.costPerHour),
      userGroupUserLink: values.userGroupUserLink.filter((link) => link.checked).map((link) => link.id),
      projectTypeUserLink: values.projectTypeUserLink.filter((link) => link.checked).map((link) => link.id),
    };

    userService.updateUser(updData, selectedValues.userId).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });
      setRefetchTable(true);
      setSelectedValues(data);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = async (values) => {
    const insertObject = {
      ...values,
      createdBy: user.userId,
      costPerHour: parseFloat(values.costPerHour),
      userGroupUserLink: values.userGroupUserLink.filter((link) => link.checked).map((link) => link.id),
      projectTypeUserLink: values.projectTypeUserLink.filter((link) => link.checked).map((link) => link.id),
    };

    userService
      .createUser(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        setSelectedValues(data);
        setRowSelectionModel(data.id);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    userService.deleteUser(deleteData, selectedValues.userId).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues({});
      setInitValues({});
    });
  };

  const loadFormData = () => {
    if (haveSelectedValues()) {
      const { userName, ...rest } = selectedValues;
      setDefaultValues({
        ...defaultValues,
        ...rest,
        userName: '',
      });
      setInitValues({
        ...defaultValues,
        ...rest,
        userName: '',
      });
    } else {
      setDefaultValues(defaultValues);
      setInitValues({});
    }
  };

  const submenuNames = useMemo(() => {
    return getSubMenuNamesByUrls(['resourceOne']);
  }, [menus]);

  return (
    <>
      <FormBase
        name="User"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <FormHeaderUI
                addClick={() => {
                  setSelectedValues({});
                  setInitValues(defaultValues);
                  setViewField(false);
                  setAddField(false);
                }}
                addDisabled={!viewField || !canEdit}
                modClick={() => {
                  setViewField(false);
                  setEditButtonvisible(false);
                }}
                modDisabled={!viewField || operatorsDisabled || !canEdit}
                deleteClick={() => {
                  setShowDeleteDialog(true);
                }}
                deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                copyClick={() => {
                  loadFormData();
                  setViewField(false);
                  setAddField(false);
                }}
                copyDisabled={!viewField || operatorsDisabled || !canEdit}
              />

              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
                //validateOnMount={false}
              >
                {({ resetForm }) => (
                  <Form>
                    <FormFields
                      viewField={viewField}
                      selectedValues={selectedValues}
                      submenuId={submenu}
                      autoCompleteData={autoCompleteData}
                      autoCompleteOptions={autoCompleteOptions}
                    />

                    <UserTabs
                      disabled={viewField}
                      selectedValues={selectedValues}
                      submenuNames={submenuNames}
                      autoCompleteOptions={autoCompleteOptions}
                      addField={addField}
                      setRefetchTable={setRefetchTable}
                    />

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);
                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <UserReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
            autoCompleteData={autoCompleteData}
            autoCompleteOptions={autoCompleteOptions}
          />
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
